import { getActiveCompanyConfig } from "domain/company/companyConfig";
import {
  calculatePanelsAndAttachmentsDraft,
  calculateQuoteDraft,
} from "domain/quote/calculateQuote";
import { redrawCanvas, SideLengthLabelRegion } from "util/canvas";

import {
  Attachment,
  AttachmentAdjustments,
  FinalCost,
  Line,
  Material,
  MaterialType,
  Point,
  Province,
} from "types/canvasTypes";

type GlassHeight = 36 | 42 | 48;
type Scale = 2.9 | 1.5 | 1;

type RenderQuoteInput = {
  context: CanvasRenderingContext2D;
  canvas: HTMLCanvasElement;
  lines: Line[];
  glassHeight: number;
  scale: number;
  chalkLine: boolean;
  gapSize: number;
  viewportZoom?: number;
  viewportOffset?: Point;
};

type QuoteSummaryInput = {
  lines: Line[];
  glassHeight: number;
  scale: number;
  chalkLine: boolean;
  gapSize: number;
  materialType: MaterialType;
  includeAttachments: boolean;
  hasDoubleDoor: boolean;
  province: Province;
};

type QuoteSummary = {
  pannels: any[];
  attachment: Attachment[];
  finalCost: FinalCost[];
  totalCost: number;
};

function normalizeHeight(height: number): GlassHeight {
  if (height === 36 || height === 42 || height === 48) {
    return height;
  }

  return 48;
}

function normalizeScale(scale: number): Scale {
  if (scale === 1 || scale === 1.5 || scale === 2.9) {
    return scale;
  }

  return 2.9;
}

export function renderQuoteToCanvas({
  context,
  canvas,
  lines,
  glassHeight,
  scale,
  chalkLine,
  gapSize,
  viewportZoom = 1,
  viewportOffset = { x: 0, y: 0 },
}: RenderQuoteInput): {
  pannels: any[];
  material: Attachment[];
  sideLengthLabels: SideLengthLabelRegion[];
} {
  if (!canvas || !context || !lines || !lines.length) {
    const redrawResult = redrawCanvas(
      lines,
      context,
      canvas,
      scale,
      viewportZoom,
      viewportOffset
    );
    return {
      pannels: [],
      material: [],
      sideLengthLabels: redrawResult?.sideLengthLabels ?? [],
    };
  }

  const companyConfig = getActiveCompanyConfig();
  const finalGap = chalkLine ? gapSize : companyConfig.rules.defaultGapSize;
  const result = calculatePanelsAndAttachmentsDraft(
    {
      lines,
      glassHeight: normalizeHeight(glassHeight),
      scale: normalizeScale(scale),
      chalkLine,
      gapSize: finalGap,
    },
    companyConfig
  );

  const redrawResult = redrawCanvas(
    lines,
    context,
    canvas,
    scale,
    viewportZoom,
    viewportOffset
  );
  return {
    pannels: result.pannels,
    material: result.attachment,
    sideLengthLabels: redrawResult?.sideLengthLabels ?? [],
  };
}

export function buildQuoteSummary(input: QuoteSummaryInput): QuoteSummary {
  const companyConfig = getActiveCompanyConfig();
  const finalGap = input.chalkLine ? input.gapSize : companyConfig.rules.defaultGapSize;

  return calculateQuoteDraft(
    {
      lines: input.lines,
      glassHeight: normalizeHeight(input.glassHeight),
      scale: normalizeScale(input.scale),
      province: input.province,
      materialType: input.materialType,
      includeAttachments: input.includeAttachments,
      chalkLine: input.chalkLine,
      gapSize: finalGap,
      hasDoubleDoor: input.hasDoubleDoor,
    },
    companyConfig
  );
}

export function applyAttachmentAdjustments(
  attachments: Attachment[],
  adjustments: AttachmentAdjustments
): Attachment[] {
  return attachments.map((attachment) => {
    const adjustment = adjustments[attachment.material] || 0;
    return {
      ...attachment,
      count: Math.max(0, attachment.count + adjustment),
    };
  });
}

export function applyAttachmentInclusionRule(
  attachments: Attachment[],
  includeAttachments: boolean
): Attachment[] {
  if (includeAttachments) {
    return attachments;
  }

  return attachments
    .filter((attachment) => attachment.material === Material.Spigots)
    .map((attachment) => ({ ...attachment }));
}

export function calculateSummaryTotal(
  finalCost: FinalCost[],
  attachments: Attachment[],
  materialType: MaterialType,
  province: Province
): number {
  const glassTotal = finalCost.reduce((sum, cost) => {
    return sum + (cost.price.get(province) || 0) * cost.total;
  }, 0);

  const attachmentTotal = attachments.reduce((sum, attachment) => {
    const unitCost =
      materialType === MaterialType.Silver
        ? attachment.costSilver.get(province) || 0
        : attachment.costBlack.get(province) || 0;

    return sum + unitCost * attachment.count;
  }, 0);

  return glassTotal + attachmentTotal;
}
